import { Component, computed, effect, inject, input, output, viewChild, ChangeDetectionStrategy } from '@angular/core';

import { Observable, map, of } from 'rxjs';

import { FinnairPassengerCode, FinnairPassengerItem } from '@fcom/dapi/api/models';
import { ButtonTheme } from '@fcom/ui-components/components/buttons';
import { CarouselComponent } from '@fcom/ui-components/components/carousel';
import { LanguageService } from '@fcom/ui-translate/services/language.service';

interface SelectablePassenger {
  id: string;
  buttonLabel: Observable<string>;
}

@Component({
  selector: 'fin-shopping-cart-passengers',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './shopping-cart-passengers.component.html',
})
export class ShoppingCartPassengersComponent {
  protected readonly ButtonTheme = ButtonTheme;

  private readonly languageService = inject(LanguageService);

  buttonCarousel = viewChild.required<CarouselComponent>('buttonCarousel');

  passengers = input<FinnairPassengerItem[]>();
  selectedPassengerId = input<string | null>(null);
  passengerSelected = output<string | null>();
  tabsVisible = output<boolean>();

  constructor() {
    effect(() => {
      this.tabsVisible.emit(this.mappedPassengers().length > 1);
    });
  }

  mappedPassengers = computed<SelectablePassenger[]>(() => {
    const currentPassengers = this.passengers() ?? [];
    const infants: FinnairPassengerItem[] = currentPassengers.filter(
      (passenger: FinnairPassengerItem) => passenger.passengerTypeCode === FinnairPassengerCode.INF
    );
    const nonInfantPassengers = currentPassengers.filter(
      (passenger: FinnairPassengerItem) => passenger.passengerTypeCode !== FinnairPassengerCode.INF
    );

    return nonInfantPassengers.map((passenger: FinnairPassengerItem, index: number) => {
      const infant = infants.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passenger.id);
      const passengerName = this.getName(passenger, nonInfantPassengers);
      return {
        id: passenger.id,
        buttonLabel: this.getButtonLabel(passengerName, infant, index + 1),
      };
    });
  });

  tabIds = computed(() => [this.getTabId(null), ...this.mappedPassengers().map((p) => this.getTabId(p.id))].join(' '));

  protected getTabId(passengerId: string | null): string {
    return 'sc-pax-btn-' + (passengerId ?? 'all');
  }

  private getButtonLabel(
    passengerName: string | undefined,
    infant: FinnairPassengerItem | undefined,
    passengerNumber: number
  ): Observable<string> {
    if (passengerName && infant?.firstName) {
      return this.languageService.translate<string>('shoppingCart.passengerAndInfant', [
        passengerName,
        infant.firstName,
      ]);
    }
    if (infant) {
      return this.languageService.translate<string>('shoppingCart.passengerWithInfant', [passengerNumber]);
    }
    if (passengerName) {
      return of(passengerName);
    }
    return this.languageService.translate<string>('shoppingCart.passenger').pipe(map((t) => `${t} ${passengerNumber}`));
  }

  private getName(passenger: FinnairPassengerItem, allPassengers: FinnairPassengerItem[]): string | undefined {
    if (!passenger.firstName) {
      return undefined;
    }

    const hasDuplicateFirstName = allPassengers.some(
      (p) => p.id !== passenger.id && p.firstName === passenger.firstName
    );

    if (hasDuplicateFirstName && passenger.lastName) {
      return `${passenger.firstName} ${passenger.lastName.charAt(0)}.`;
    }

    return passenger.firstName;
  }

  selectPassenger(passengerId: string | null): void {
    this.scrollButtonIntoView(passengerId);
    this.passengerSelected.emit(passengerId);
  }

  private scrollButtonIntoView(passengerId: string | null): void {
    this.buttonCarousel().scrollToElement(this.getTabId(passengerId));
  }
}
